import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { debounce } from 'lodash'
import SimpleBar from 'simplebar-react'
import {
  XClose,
  SearchMd,
  // Plus, // re-enable together with the "Add new employee" button below
  User01,
  RefreshCcw02,
} from '@untitled-ui/icons-react'
import {
  // MyButton, // re-enable together with the "Add new employee" button below
  MyTextField,
  MyHorizontalTabV2,
  MyAvatar,
  myToaster,
} from '@interstellar-component'
import { useReportEnquiry } from '../Context'
import EmployeeDetailHoverCard from './EmployeeDetailHoverCard'
import MyConsentStatusChip from '../../Workforce/components/MyConsentStatusChip'
import Service from '../service'

const PAGE_SIZE = 4

function EmployeeListSlider({ onSelect }) {
  const { popSlider, sliderStack } = useReportEnquiry()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusTab, setStatusTab] = useState('All status')
  const [employeeGroups, setEmployeeGroups] = useState([])
  const [loading, setLoading] = useState(false)
  // Per-company "view more" pagination — only used when the slider shows
  // more than one company (matches the design: single-company users see the
  // full list without a "View more" button).
  const [visibleCounts, setVisibleCounts] = useState({})

  // Debounced setter so we hit the BE once per typing burst, not per keystroke.
  const debouncedSetSearch = useMemo(
    () => debounce((v) => setSearchTerm(v), 500),
    []
  )
  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch])

  // Hover Card state
  const [hoveredEmployee, setHoveredEmployee] = useState(null)
  const [isHoverCardVisible, setIsHoverCardVisible] = useState(false)
  const hoverTimeoutRef = React.useRef(null)

  const sliderIndex = sliderStack.findIndex(
    (s) => s.current === 'employee-list'
  )
  const currentOffset = sliderIndex >= 0 ? sliderIndex * 420 : 0

  // ── Fetch employees from API ──────────────────────────────────────────
  const fetchEmployees = useCallback(() => {
    setLoading(true)
    const params = {}
    if (searchTerm) params.search = searchTerm
    if (statusTab !== 'All status') {
      params.consent_status = statusTab.toLowerCase()
    }

    Service.getEmployees(params)
      .then((res) => {
        const groups = (res.data || []).map((company) => ({
          entity: company.name,
          employees: (company.employees || []).map((emp) => ({
            id: emp.id,
            employeeId: emp.employee_id,
            label: emp.full_name,
            position:
              emp.position?.name || emp.employment_level?.name || '-',
            consentStatus: emp.consent_status,
            status:
              emp.consent_status === 'active'
                ? 'Active'
                : emp.consent_status === 'expired'
                  ? 'Expired'
                  : emp.consent_status === 'new'
                    ? 'New'
                    : emp.consent_status === 'pending'
                      ? 'Pending'
                      : 'Expired',
            avatar: null,
            email: emp.email,
            whatsapp: emp.phone,
            entityObj: {
              label: company.name,
              value: company.id,
            },
            level: emp.employment_level
              ? {
                  label: emp.employment_level.name,
                  value: emp.employment_level.id,
                }
              : null,
            positionObj: emp.position
              ? { label: emp.position.name, value: emp.position.id }
              : null,
            division: null,
            _raw: emp,
          })),
        }))
        setEmployeeGroups(groups)
      })
      .catch(myToaster)
      .finally(() => setLoading(false))
  }, [searchTerm, statusTab])

  useEffect(() => {
    fetchEmployees()
  }, [fetchEmployees])

  // Reset pagination when the underlying list changes (new filter/search/data)
  // so the user always starts at the first PAGE_SIZE rows per company.
  useEffect(() => {
    setVisibleCounts({})
  }, [employeeGroups, searchTerm, statusTab])

  const handleClose = () => popSlider()

  const handleMouseEnter = (emp) => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current)
    setHoveredEmployee(emp)
    setIsHoverCardVisible(true)
  }

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHoverCardVisible(false)
    }, 300)
  }

  const handleModalKeepAlive = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current)
    setIsHoverCardVisible(true)
  }

  const handleSelectEmployee = (emp) => {
    onSelect?.({
      label: emp.label,
      value: emp.id,
      supportingText: emp.position,
      email: emp.email,
      whatsapp: emp.whatsapp,
      entity: emp.entityObj,
      level: emp.level,
      position: emp.positionObj,
      // Identity on file — prefills the HR e-KTP form when the holding runs
      // ektp_upload_process='hr' (NewEnquirySlider.handleEmployeeChange).
      full_name: emp.label,
      id_number: emp._raw?.id_number || null,
      date_of_birth: emp._raw?.date_of_birth || null,
      gender: emp._raw?.gender || null,
      address: emp._raw?.address || null,
      // Last captured e-KTP (repeat enquiry): name as printed, CLIK region and
      // the previous KTP photo. kyc_record_id lets the BE copy that photo onto
      // the new enquiry instead of HR re-uploading it.
      ktp_name: emp._raw?.ktp_name || null,
      city: emp._raw?.city || null,
      district: emp._raw?.district || null,
      subdistrict: emp._raw?.subdistrict || null,
      postal_code: emp._raw?.postal_code || null,
      ktp_photo_url: emp._raw?.ktp_photo_url || null,
      kyc_record_id: emp._raw?.kyc_record_id || null,
    })
    popSlider()
  }

  const filteredGroups = employeeGroups
    .map((group) => ({
      ...group,
      employees: group.employees.filter((emp) => {
        const matchesStatus =
          statusTab === 'All status' || emp.status === statusTab
        return matchesStatus
      }),
    }))
    .filter((group) => group.employees.length > 0)

  return (
    <div className="flex h-screen w-[420px] flex-col bg-white shadow-xl">
      {/* Header */}
      <header className="relative flex items-start gap-x-4 px-6 py-6 border-b border-gray-100">
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-[12px] top-[12px] flex h-10 w-10 items-center justify-center rounded-lg p-2 text-gray-400 hover:bg-gray-50 active:bg-gray-100"
        >
          <XClose size={24} stroke="currentColor" />
        </button>

        <div className="flex flex-1 flex-col gap-1 pt-1">
          <p className="text-lg font-semibold text-gray-900">
            List of employee
          </p>
          <p className="text-sm text-gray-500 font-medium">
            Select an employee for the enquiry
          </p>
        </div>
      </header>

      {/* Top Controls */}
      <div className="flex flex-col gap-4 p-6 border-b border-gray-100">
        <MyTextField
          placeholder="Search by name, ID, email, or phone"
          onChangeForm={(e) => debouncedSetSearch(e.target.value)}
          startAdornment={
            <SearchMd className="size-4 text-gray-400" />
          }
        />

        <div className="w-full overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          <MyHorizontalTabV2
            fitContent
            value={statusTab}
            onChange={setStatusTab}
            tabs={[
              { label: 'All status', value: 'All status' },
              { label: 'Active', value: 'Active' },
              { label: 'Expired', value: 'Expired' },
              { label: 'Pending', value: 'Pending' },
              { label: 'New', value: 'New' },
            ]}
          />
        </div>

        {/* "Add new employee" hidden until creating an employee from the
            report-enquiry flow is supported (the button had no action).
            Re-enable with the Plus import above.
        <MyButton
          variant="text"
          color="primary"
          size="sm"
          customClassname="gap-2 w-max px-0 font-semibold"
        >
          <Plus className="size-4" />
          Add new employee
        </MyButton> */}
      </div>

      {/* List Content */}
      <div className="flex-1 overflow-hidden">
        <SimpleBar forceVisible="y" style={{ maxHeight: '100%' }}>
          <div className="flex flex-col">
            {loading && (
              <div className="flex items-center justify-center py-8">
                <p className="text-sm text-gray-500">Loading...</p>
              </div>
            )}
            {!loading && (() => {
              const isMultiCompany = filteredGroups.length > 1
              return filteredGroups.map((group) => {
                const visible = isMultiCompany
                  ? (visibleCounts[group.entity] ?? PAGE_SIZE)
                  : group.employees.length
                const shownEmployees = group.employees.slice(0, visible)
                const hasMore = visible < group.employees.length
                return (
                  <div key={group.entity} className="flex flex-col">
                    <div className="px-6 py-2 bg-gray-50 border-b border-gray-100">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        {group.entity}
                      </p>
                    </div>
                    {shownEmployees.map((emp) => {
                      // Selectable: New (never enquired), Expired (consent lapsed), and
                      // Active (valid consent) — a re-enquiry on an Active employee starts
                      // a fresh monitoring cycle, and the BE takes over the existing row
                      // in place (createEmployeeEnquiry re-enquiry path), so it never 400s.
                      // Only Pending (awaiting_form) stays disabled: an invite is already
                      // in flight for it, so the right action there is Resend, not a new
                      // enquiry that would reset the consent collection under the employee.
                      const isDisabled = emp.status === 'Pending'
                      return (
                        <button
                          key={`${group.entity}-${emp.id}`}
                          type="button"
                          onClick={() => {
                            if (isDisabled) return
                            handleSelectEmployee(emp)
                          }}
                          onMouseEnter={() => handleMouseEnter(emp)}
                          onMouseLeave={handleMouseLeave}
                          aria-disabled={isDisabled}
                          className={`flex items-center gap-3 px-6 py-4 border-b border-gray-50 text-left transition-colors ${
                            isDisabled
                              ? 'cursor-not-allowed opacity-60 hover:bg-gray-25'
                              : 'hover:bg-gray-50'
                          }`}
                        >
                          <MyAvatar
                            name={emp.label}
                            src={emp.avatar}
                            size="md"
                            fallback={
                              <User01 className="size-5 text-gray-400" />
                            }
                          />
                          <div className="flex flex-1 flex-col gap-0.5">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-gray-900 whitespace-pre-wrap leading-tight">
                                {emp.label}
                                <br />
                                <span className="text-xs text-gray-500 font-normal">
                                  {emp.employeeId}
                                </span>
                              </p>
                              {emp.consentStatus && (
                                <MyConsentStatusChip status={emp.consentStatus} />
                              )}
                            </div>
                            <p className="text-xs text-gray-500">
                              {emp.position}
                            </p>
                          </div>
                        </button>
                      )
                    })}
                    {hasMore && (
                      <button
                        type="button"
                        onClick={() =>
                          setVisibleCounts((prev) => ({
                            ...prev,
                            [group.entity]: visible + PAGE_SIZE,
                          }))
                        }
                        className="flex items-center justify-center gap-2 py-3 border-b border-gray-100 text-sm font-semibold text-brand/700 hover:bg-gray-50"
                      >
                        <RefreshCcw02 className="size-4" />
                        View more
                      </button>
                    )}
                  </div>
                )
              })
            })()}
            {!loading && filteredGroups.length === 0 && (
              <div className="flex items-center justify-center py-8">
                <p className="text-sm text-gray-500">
                  No employees found
                </p>
              </div>
            )}
          </div>
        </SimpleBar>
      </div>

      {/* Hover Modal */}
      <EmployeeDetailHoverCard
        employee={hoveredEmployee}
        isVisible={isHoverCardVisible}
        onMouseEnter={handleModalKeepAlive}
        onMouseMove={handleModalKeepAlive}
        onMouseLeave={handleMouseLeave}
        onClose={() => {
          if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current)
          setIsHoverCardVisible(false)
          setHoveredEmployee(null)
        }}
        style={{
          right: `${currentOffset + 416}px`,
          top: '24px',
        }}
      />
    </div>
  )
}

export default EmployeeListSlider
